/**
 * Combat events → sounds (audio-direction §2, §3; D-044). Reads the event ring with its own cursor:
 * - `fire` / `torpedoLaunch`: the weapon's `sfx` sound. The player's own shots go through the hull chain at the
 *   top priority; everyone else's are positional (near, distant or silent by camera distance).
 * - `hitShield` / `hitHull`: hits on the player are incoming hits through the hull; others are positional.
 * - `explode`: ships at least `largeExplosionSize` big → large, smaller ships and torpedoes → small, positional
 *   (the player's own death too: the hull is gone). Big close ones duck the music (engine).
 * - `boostStart`: the player's own boost surge through the hull; others positional.
 * While reading, it fills a `CombatSummary` for the alarms and the music (hits, kills, deaths, waves, locks).
 * Allocation-free per call (D-014).
 */
import { EV, EVENT_MASK, firstReadable, type EventRing } from '../combat/events'
import { ROUTE, type Route } from './engine'
import { soundIndex, type SfxConfig } from './sfxSchema'
import { PRIORITY, type Priority } from './voices'

/** The part of the engine the router plays through (the engine, or a recorder in tests). */
export interface SoundSink {
  play(sound: number, gain: number, route: Route, priority: Priority, pan?: number, rate?: number, when?: number): number
  playWorld(sound: number, gain: number, x: number, y: number, z: number, priority: Priority, when?: number): number
}

/** What happened to and around the player since the last read (reset by the reader each frame). */
export interface CombatSummary {
  playerFired: number
  /** Hits taken by the player (shield or hull). */
  playerHit: number
  /** Ships the player killed. */
  playerKills: number
  playerDied: boolean
  playerRespawned: boolean
  /** The wave number that started, or 0. */
  waveStarted: number
  shieldDown: boolean
  /** Someone started a lock on the player. */
  incomingLock: boolean
  /** Raider shots fired anywhere. */
  enemyFired: number
}

export function createCombatSummary(): CombatSummary {
  return { playerFired: 0, playerHit: 0, playerKills: 0, playerDied: false, playerRespawned: false, waveStarted: 0, shieldDown: false, incomingLock: false, enemyFired: 0 }
}

export function resetCombatSummary(s: CombatSummary): void {
  s.playerFired = 0
  s.playerHit = 0
  s.playerKills = 0
  s.playerDied = false
  s.playerRespawned = false
  s.waveStarted = 0
  s.shieldDown = false
  s.incomingLock = false
  s.enemyFired = 0
}

export interface AudioRouter {
  readonly config: SfxConfig
  /** Sound index per weapon index (from `WeaponDef.sfx`). */
  readonly weaponSound: Int32Array
  readonly shieldHit: number
  readonly hullHit: number
  readonly explosionSmall: number
  readonly explosionLarge: number
  readonly boostStart: number
  /** Ring cursor. */
  cursor: number
}

/** Maps every weapon's `sfx` key to the bank. Throws naming the weapon when a key is missing. */
export function createAudioRouter(weapons: readonly { id: string; sfx: string }[], config: SfxConfig): AudioRouter {
  const weaponSound = new Int32Array(weapons.length)
  for (let i = 0; i < weapons.length; i++) {
    const s = soundIndex(weapons[i].sfx)
    if (s < 0) throw new Error(`weapon ${weapons[i].id}: sfx "${weapons[i].sfx}" is not in the sound bank (src/audio/sfx.json)`)
    weaponSound[i] = s
  }
  return {
    config,
    weaponSound,
    shieldHit: soundIndex('shieldHit'),
    hullHit: soundIndex('hullHit'),
    explosionSmall: soundIndex('explosionSmall'),
    explosionLarge: soundIndex('explosionLarge'),
    boostStart: soundIndex('boostStart'),
    cursor: 0,
  }
}

/** Skips every event emitted so far (audio not running, or paused). */
export function skipEvents(router: AudioRouter, ring: EventRing): void {
  router.cursor = ring.head
}

/**
 * Plays every new event and adds it to `summary` (not reset here). `player` is the player's entity (-1 if none).
 * Returns the number of plays attempted.
 */
export function routeEvents(router: AudioRouter, ring: EventRing, player: number, sink: SoundSink, summary: CombatSummary): number {
  let plays = 0
  for (let i = firstReadable(ring, router.cursor); i < ring.head; i++) {
    const s = i & EVENT_MASK
    const type = ring.type[s]
    const eid = ring.eid[s]
    const own = player >= 0 && eid === player
    const x = ring.x[s]
    const y = ring.y[s]
    const z = ring.z[s]
    switch (type) {
      case EV.fire:
      case EV.torpedoLaunch: {
        const w = ring.weapon[s]
        const sound = w < router.weaponSound.length ? router.weaponSound[w] : -1
        if (own) summary.playerFired++
        else summary.enemyFired++
        if (sound < 0) break
        if (own) sink.play(sound, 1, ROUTE.hull, PRIORITY.ownFire)
        else sink.playWorld(sound, 1, x, y, z, PRIORITY.near)
        plays++
        break
      }
      case EV.hitShield:
      case EV.hitHull: {
        const sound = type === EV.hitShield ? router.shieldHit : router.hullHit
        if (own) {
          summary.playerHit++
          sink.play(sound, 1, ROUTE.hull, PRIORITY.incoming)
        } else sink.playWorld(sound, 1, x, y, z, PRIORITY.near)
        plays++
        break
      }
      case EV.explode: {
        const big = eid >= 0 && ring.amount[s] >= router.config.largeExplosionSize
        sink.playWorld(big ? router.explosionLarge : router.explosionSmall, 1, x, y, z, own ? PRIORITY.incoming : PRIORITY.near)
        plays++
        break
      }
      case EV.boostStart:
        if (own) sink.play(router.boostStart, 1, ROUTE.hull, PRIORITY.near)
        else sink.playWorld(router.boostStart, 1, x, y, z, PRIORITY.near)
        plays++
        break
      case EV.kill:
        if (own) summary.playerDied = true
        else if (player >= 0 && ring.other[s] === player) summary.playerKills++
        break
      case EV.respawn:
        if (own) summary.playerRespawned = true
        break
      case EV.waveStart:
        summary.waveStarted = ring.amount[s]
        break
      case EV.shieldDown:
        if (own) summary.shieldDown = true
        break
      case EV.lockStart:
        if (player >= 0 && !own && ring.other[s] === player) summary.incomingLock = true
        break
      default:
        break
    }
  }
  router.cursor = ring.head
  return plays
}
